<template>
  <div class="holiday-calendar-settings">
    <CCard class="holiday-calendar-card">
      <CCardHeader>
        <div class="holiday-card-heading">
          <div class="section-heading">
            <span class="section-icon"><CIcon name="cil-calendar"/></span>
            <div>
              <strong>休日カレンダー</strong>
              <p>法定休日、毎週の会社休日、日付ごとの休日を現在の会社だけに設定します。</p>
            </div>
          </div>
          <CBadge color="info">企業別設定</CBadge>
        <SettingSectionAction
          v-if="compact"
          :to="{ name: '自社設定の休日カレンダー' }"
        />
        </div>
      </CCardHeader>

      <CCardBody v-if="!compact">
        <CAlert
          v-if="errorMessage"
          color="danger"
          close-button
          @update:show="errorMessage = ''"
        >
          {{ errorMessage }}
        </CAlert>
        <CAlert
          v-if="successMessage"
          color="success"
          close-button
          @update:show="successMessage = ''"
        >
          {{ successMessage }}
        </CAlert>

        <div v-if="loadingInitial" class="calendar-loading">
          <CSpinner color="primary"/>
          <span>休日カレンダーを読み込んでいます。</span>
        </div>

        <div v-else-if="!calendarLoaded" class="calendar-load-failed">
          <CIcon name="cil-warning" size="2xl"/>
          <strong>休日カレンダーを表示できませんでした。</strong>
          <p>通信状態と権限を確認して、もう一度読み込んでください。</p>
          <CButton color="primary" variant="outline" @click="retryLoad">再読み込み</CButton>
        </div>

        <template v-else>
          <section class="calendar-section" aria-labelledby="weekly-holiday-heading">
            <div class="calendar-section-heading">
              <div>
                <p class="section-kicker">WEEKLY HOLIDAYS</p>
                <h3 id="weekly-holiday-heading">毎週の休日</h3>
                <p>法定休日は1曜日、会社休日は複数曜日を選択できます。</p>
              </div>
              <CBadge :color="weeklyDirty ? 'warning' : 'success'">
                {{ weeklyDirty ? '未保存の変更あり' : '保存済み' }}
              </CBadge>
            </div>

            <CAlert color="info">
              法定休日と会社休日に同じ曜日は設定できません。設定変更後も、すでに保存済みの勤務表入力は自動で書き換えません。
            </CAlert>

            <div class="weekly-setting-grid">
              <div class="weekly-setting-panel weekly-setting-panel--statutory">
                <label for="statutoryHolidayDay">法定休日<span class="required-mark">*</span></label>
                <select
                  id="statutoryHolidayDay"
                  v-model.number="weekly.statutoryDay"
                  class="form-control"
                  :disabled="savingWeekly"
                  @change="onStatutoryDayChanged"
                >
                  <option v-for="day in weekDays" :key="day.value" :value="day.value">
                    {{ day.label }}曜日
                  </option>
                </select>
                <small>法定休日時間の集計と、勤務表の赤系表示に使用します。</small>
              </div>

              <fieldset class="weekly-setting-panel weekly-setting-panel--company">
                <legend>毎週の会社休日</legend>
                <p>休業日として扱う曜日を選択してください。0曜日でも登録できます。</p>
                <div class="weekday-choice-grid">
                  <label
                    v-for="day in weekDays"
                    :key="day.value"
                    class="weekday-choice"
                    :class="{
                      'is-selected': weekly.companyDays.includes(day.value),
                      'is-disabled': weekly.statutoryDay === day.value
                    }"
                  >
                    <input
                      v-model="weekly.companyDays"
                      type="checkbox"
                      :value="day.value"
                      :disabled="savingWeekly || weekly.statutoryDay === day.value"
                    >
                    <span>{{ day.shortLabel }}</span>
                    <small>{{ day.label }}曜日</small>
                  </label>
                </div>
              </fieldset>
            </div>

            <div class="weekly-summary" aria-live="polite">
              <div>
                <span>法定休日</span>
                <strong>{{ dayLabel(weekly.statutoryDay) }}曜日</strong>
              </div>
              <div>
                <span>会社休日</span>
                <strong>{{ companyHolidayLabel }}</strong>
              </div>
            </div>

            <div class="calendar-section-actions">
              <CButton
                color="primary"
                :disabled="savingWeekly || !weeklyDirty"
                @click="saveWeeklyHolidays"
              >
                {{ savingWeekly ? '保存しています' : '毎週の休日を保存' }}
              </CButton>
            </div>
          </section>

          <section class="calendar-section calendar-section--master" aria-labelledby="holiday-master-heading">
            <div class="calendar-section-heading holiday-master-heading">
              <div>
                <p class="section-kicker">HOLIDAY MASTER</p>
                <h3 id="holiday-master-heading">日付ごとの休日</h3>
                <p>共通休日を会社単位で変更・非表示にし、自社独自の休日も追加できます。</p>
                <label class="national-holiday-switch" for="national-holiday">
                  <input
                    id="national-holiday"
                    type="checkbox"
                    :checked="nationalHolidayEnabled"
                    :disabled="savingNationalHoliday || loadingCalendar"
                    @change="toggleNationalHoliday($event.target.checked)"
                  />
                  <span>
                    <strong>祝日を休みにする</strong>
                    <small>
                      切ると、共通休日（国民の祝日）が一覧から消えます。
                      祝日に営業する会社は、1日ずつ非表示にしなくて済みます。
                      <b>「休日を追加」で足した自社の休日は残ります。</b>
                      祝日のうち特定の日だけ休みにしたい場合も、
                      その日を「休日を追加」で足してください。
                    </small>
                  </span>
                </label>
              </div>
              <CButton color="primary" @click="openCreateHoliday">休日を追加</CButton>
            </div>

            <div class="year-toolbar">
              <div class="year-navigation" aria-label="表示年の変更">
                <CButton
                  color="secondary"
                  variant="outline"
                  :disabled="loadingCalendar || selectedYear <= 2000"
                  @click="shiftYear(-1)"
                >
                  前年
                </CButton>
                <label for="holidayCalendarYear">表示年</label>
                <input
                  id="holidayCalendarYear"
                  v-model.number="yearInput"
                  type="number"
                  min="2000"
                  max="2100"
                  class="form-control year-input"
                  :disabled="loadingCalendar"
                  @keyup.enter="applyYearInput"
                >
                <CButton
                  color="secondary"
                  variant="outline"
                  :disabled="loadingCalendar"
                  @click="applyYearInput"
                >
                  表示
                </CButton>
                <CButton
                  color="secondary"
                  variant="outline"
                  :disabled="loadingCalendar || selectedYear >= 2100"
                  @click="shiftYear(1)"
                >
                  翌年
                </CButton>
              </div>
              <CButton
                color="secondary"
                variant="outline"
                :disabled="loadingCalendar || selectedYear === currentYear"
                @click="selectYear(currentYear)"
              >
                今年へ戻る
              </CButton>
            </div>

            <CAlert color="info">
              「共通休日」は全社共通マスタを直接変更しません。編集・削除した内容は、現在の会社だけの変更または非表示として保存されます。
            </CAlert>

            <div v-if="loadingCalendar" class="calendar-loading calendar-loading--compact">
              <CSpinner color="primary"/>
              <span>{{ selectedYear }}年の休日を読み込んでいます。</span>
            </div>

            <template v-else>
              <div class="holiday-list-summary">
                <strong>{{ selectedYear }}年</strong>
                <span>{{ holidays.length }}件の休日</span>
              </div>

              <div v-if="holidays.length" class="holiday-table-wrap">
                <table class="holiday-table">
                  <thead>
                    <tr>
                      <th scope="col">日付</th>
                      <th scope="col">休日名</th>
                      <th scope="col">設定元</th>
                      <th scope="col" class="holiday-actions-column">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="holiday in holidays" :key="holiday.date">
                      <td data-label="日付">
                        <strong>{{ formatHolidayDate(holiday.date) }}</strong>
                      </td>
                      <td data-label="休日名">{{ holiday.name }}</td>
                      <td data-label="設定元">
                        <CBadge :color="holidaySourceColor(holiday)">
                          {{ holidaySourceLabel(holiday) }}
                        </CBadge>
                      </td>
                      <td data-label="操作" class="holiday-actions">
                        <CButton
                          color="primary"
                          variant="outline"
                          size="sm"
                          @click="openEditHoliday(holiday)"
                        >
                          編集
                        </CButton>
                        <CButton
                          color="danger"
                          variant="outline"
                          size="sm"
                          @click="openDeleteHoliday(holiday)"
                        >
                          {{ holiday.globalDefault ? '自社で非表示' : '削除' }}
                        </CButton>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div v-else class="holiday-empty-state">
                <CIcon name="cil-calendar" size="2xl"/>
                <strong>{{ selectedYear }}年に表示する休日はありません。</strong>
                <p>自社独自の休日を登録する場合は「休日を追加」を押してください。</p>
                <CButton color="primary" variant="outline" @click="openCreateHoliday">最初の休日を追加</CButton>
              </div>
            </template>
          </section>
        </template>
      </CCardBody>
    </CCard>

    <transition name="modal">
      <div
        v-if="showHolidayModal"
        class="modal-mask holiday-editor-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="holiday-editor-title"
        @click.self="closeHolidayModal"
      >
        <div class="modal-wrapper">
          <form class="modal-container" @submit.prevent="saveHoliday">
            <div class="modal-heading">
              <p class="section-kicker">HOLIDAY EDITOR</p>
              <h2 id="holiday-editor-title">
                {{ holidayForm.originalDate ? '休日を編集' : '休日を追加' }}
              </h2>
              <p>この変更は現在ログインしている会社だけに適用されます。</p>
            </div>

            <CAlert v-if="holidayFormError" color="danger">
              {{ holidayFormError }}
            </CAlert>

            <div class="holiday-editor-grid">
              <div>
                <label for="holidayDate">日付<span class="required-mark">*</span></label>
                <input
                  id="holidayDate"
                  v-model="holidayForm.holidayDate"
                  type="date"
                  min="2000-01-01"
                  max="2100-12-31"
                  class="form-control"
                  :disabled="savingHoliday"
                  required
                >
              </div>
              <div>
                <label for="holidayName">休日名<span class="required-mark">*</span></label>
                <input
                  id="holidayName"
                  v-model.trim="holidayForm.name"
                  type="text"
                  maxlength="120"
                  class="form-control"
                  :disabled="savingHoliday"
                  placeholder="例：創立記念日"
                  required
                >
                <small>{{ holidayForm.name.length }}/120文字</small>
              </div>
            </div>

            <CAlert v-if="editingGlobalHoliday" color="warning">
              共通休日の元データは変更せず、現在の会社専用の名称・日付変更として保存します。
            </CAlert>

            <div class="modal-actions">
              <CButton
                type="button"
                color="secondary"
                variant="outline"
                :disabled="savingHoliday"
                @click="closeHolidayModal"
              >
                キャンセル
              </CButton>
              <CButton type="submit" color="primary" :disabled="savingHoliday">
                {{ savingHoliday ? '保存しています' : '保存する' }}
              </CButton>
            </div>
          </form>
        </div>
      </div>
    </transition>

    <transition name="modal">
      <div
        v-if="deleteTarget"
        class="modal-mask holiday-delete-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="holiday-delete-title"
        @click.self="closeDeleteModal"
      >
        <div class="modal-wrapper">
          <div class="modal-container modal-container--small">
            <div class="modal-heading">
              <p class="section-kicker">CONFIRM DELETE</p>
              <h2 id="holiday-delete-title">
                {{ deleteTarget.globalDefault ? 'この会社で休日を非表示にしますか？' : '休日を削除しますか？' }}
              </h2>
            </div>

            <dl class="delete-summary">
              <div><dt>日付</dt><dd>{{ formatHolidayDate(deleteTarget.date) }}</dd></div>
              <div><dt>休日名</dt><dd>{{ deleteTarget.name }}</dd></div>
            </dl>

            <CAlert :color="deleteTarget.globalDefault ? 'warning' : 'danger'">
              <template v-if="deleteTarget.globalDefault">
                全社共通マスタからは削除せず、現在の会社だけで非表示にします。他社には影響しません。
              </template>
              <template v-else>
                現在の会社に追加した休日を削除します。他社には影響しません。
              </template>
            </CAlert>

            <div class="modal-actions">
              <CButton
                type="button"
                color="secondary"
                variant="outline"
                :disabled="deletingHoliday"
                @click="closeDeleteModal"
              >
                キャンセル
              </CButton>
              <CButton color="danger" :disabled="deletingHoliday" @click="deleteHoliday">
                {{ deletingHoliday ? '処理しています' : (deleteTarget.globalDefault ? '自社で非表示にする' : '削除する') }}
              </CButton>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import SettingSectionAction from './SettingSectionAction.vue'
import api from '@/services/holidayCalendar'

const WEEK_DAYS = [
  { value: 1, label: '月', shortLabel: '月' },
  { value: 2, label: '火', shortLabel: '火' },
  { value: 3, label: '水', shortLabel: '水' },
  { value: 4, label: '木', shortLabel: '木' },
  { value: 5, label: '金', shortLabel: '金' },
  { value: 6, label: '土', shortLabel: '土' },
  { value: 7, label: '日', shortLabel: '日' }
]

const emptyHolidayForm = () => ({
  originalDate: null,
  holidayDate: '',
  name: '',
  globalDefault: false
})

export default {
  name: 'HolidayCalendarSettings',

  components: { SettingSectionAction },

  /**
   * 自社設定の一覧では **たたんで出す。**
   *
   * たたまないころは、この部品だけで縦 577〜1,389px を使っていて、
   * その中に自前の保存ボタンがあった。一覧に保存ボタンが散らばると、
   * どこを押せば何が決まるのか画面ごとに探すことになる。
   *
   * たたんだときは見出しと「変更」だけを出し、**中身も保存ボタンも
   * 出さない。**直すのは専用の画面で、決定はそこに1つだけ置く。
   */
  props: {
    compact: { type: Boolean, default: false }
  },
  data () {
    const currentYear = new Date().getFullYear()
    return {
      currentYear,
      selectedYear: currentYear,
      yearInput: currentYear,
      weekDays: WEEK_DAYS,
      weekly: {
        statutoryDay: 7,
        companyDays: [6]
      },
      originalWeekly: {
        statutoryDay: 7,
        companyDays: [6]
      },
      holidays: [],
      calendarLoaded: false,
      loadingInitial: true,
      loadingCalendar: false,
      // **既定は「休みにする」。**読めないうちに外して見せない。
      nationalHolidayEnabled: true,
      savingNationalHoliday: false,
      savingWeekly: false,
      savingHoliday: false,
      deletingHoliday: false,
      errorMessage: '',
      successMessage: '',
      showHolidayModal: false,
      holidayForm: emptyHolidayForm(),
      holidayFormError: '',
      deleteTarget: null
    }
  },
  computed: {
    normalizedCompanyDays () {
      return this.normalizeDays(this.weekly.companyDays)
    },
    weeklyDirty () {
      return Number(this.weekly.statutoryDay) !== Number(this.originalWeekly.statutoryDay) ||
        this.normalizedCompanyDays.join(',') !== this.normalizeDays(this.originalWeekly.companyDays).join(',')
    },
    companyHolidayLabel () {
      if (!this.normalizedCompanyDays.length) return '設定なし'
      return this.normalizedCompanyDays.map(day => this.dayLabel(day) + '曜日').join('、')
    },
    editingGlobalHoliday () {
      return Boolean(this.holidayForm.originalDate && this.holidayForm.globalDefault)
    }
  },
  mounted () {
    this.loadCalendar(this.currentYear, { initial: true })
  },
  methods: {
    async loadCalendar (year, options = {}) {
      const targetYear = Number(year)
      if (!this.isValidYear(targetYear)) {
        this.errorMessage = '表示年は2000年から2100年の範囲で入力してください。'
        return false
      }
      if (options.initial) this.loadingInitial = true
      else this.loadingCalendar = true
      this.errorMessage = ''
      try {
        const result = await api.get(targetYear)
        const statutoryDay = this.isIsoDay(result && result.statutory_holiday_day)
          ? Number(result.statutory_holiday_day)
          : 7
        const companyDays = this.normalizeDays(result && result.company_holiday_days)
          .filter(day => day !== statutoryDay)
        if (!options.preserveWeekly) {
          this.weekly = { statutoryDay, companyDays }
          this.originalWeekly = { statutoryDay, companyDays: companyDays.slice() }
        }
        /*
         * **読めないうちは「休みにする」。**通信の失敗で、
         * いま祝日を休みにしている会社の切替が勝手に外れて見えない。
         */
        this.nationalHolidayEnabled =
          !result || result.national_holiday_flg !== false
        this.holidays = this.normalizeHolidays(result && result.holidays)
        this.selectedYear = targetYear
        this.yearInput = targetYear
        this.calendarLoaded = true
        return true
      } catch (error) {
        this.calendarLoaded = options.initial ? false : this.calendarLoaded
        this.handleError(error, '休日カレンダーを取得できませんでした。')
        return false
      } finally {
        this.loadingInitial = false
        this.loadingCalendar = false
      }
    },
    retryLoad () {
      this.loadCalendar(this.yearInput, { initial: true })
    },
    async selectYear (year) {
      const targetYear = Number(year)
      if (!this.isValidYear(targetYear)) {
        this.errorMessage = '表示年は2000年から2100年の範囲で入力してください。'
        return
      }
      if (targetYear === this.selectedYear) {
        this.yearInput = targetYear
        return
      }
      if (this.weeklyDirty && !window.confirm('未保存の毎週休日設定が破棄されます。表示年を変更しますか？')) {
        this.yearInput = this.selectedYear
        return
      }
      await this.loadCalendar(targetYear)
    },
    applyYearInput () {
      this.selectYear(this.yearInput)
    },
    shiftYear (amount) {
      this.selectYear(this.selectedYear + amount)
    },
    onStatutoryDayChanged () {
      const statutoryDay = Number(this.weekly.statutoryDay)
      this.weekly.companyDays = this.normalizeDays(this.weekly.companyDays)
        .filter(day => day !== statutoryDay)
    },
    async toggleNationalHoliday (enabled) {
      if (this.savingNationalHoliday) return
      this.savingNationalHoliday = true
      this.errorMessage = ''
      const before = this.nationalHolidayEnabled
      this.nationalHolidayEnabled = enabled
      try {
        await api.updateNationalHoliday({ enabled })
        // 一覧そのものが変わるので読み直す。
        await this.loadCalendar(this.selectedYear, { preserveWeekly: true })
        this.successMessage = enabled
          ? '祝日を休みとして扱います。'
          : '祝日を休みとして扱いません。個別に足した休日は残ります。'
      } catch (error) {
        /*
         * **押した見た目を元へ戻す。**保存できていないのに入った
         * ままだと、画面と実際の設定が食い違う。
         */
        this.nationalHolidayEnabled = before
        this.errorMessage = '祝日の扱いを保存できませんでした。時間をおいて開き直してください。'
      } finally {
        this.savingNationalHoliday = false
      }
    },

    async saveWeeklyHolidays () {
      const statutoryDay = Number(this.weekly.statutoryDay)
      const companyDays = this.normalizedCompanyDays.filter(day => day !== statutoryDay)
      if (!this.isIsoDay(statutoryDay)) {
        this.errorMessage = '法定休日の曜日を選択してください。'
        return
      }
      const summary = `法定休日：${this.dayLabel(statutoryDay)}曜日\n会社休日：${companyDays.length ? companyDays.map(day => this.dayLabel(day) + '曜日').join('、') : '設定なし'}`
      if (!window.confirm(`毎週の休日を次の内容で保存しますか？\n\n${summary}`)) return

      this.savingWeekly = true
      this.errorMessage = ''
      this.successMessage = ''
      try {
        await api.updateWeekly({
          statutory_holiday_day: statutoryDay,
          company_holiday_days: companyDays
        })
        this.originalWeekly = { statutoryDay, companyDays: companyDays.slice() }
        this.weekly = { statutoryDay, companyDays: companyDays.slice() }
        this.successMessage = '毎週の休日設定を保存しました。勤務表と営業日計算には現在の会社の設定が使用されます。'
      } catch (error) {
        this.handleError(error, '毎週の休日設定を保存できませんでした。')
      } finally {
        this.savingWeekly = false
      }
    },
    openCreateHoliday () {
      this.holidayForm = emptyHolidayForm()
      this.holidayFormError = ''
      this.showHolidayModal = true
    },
    openEditHoliday (holiday) {
      this.holidayForm = {
        originalDate: holiday.date,
        holidayDate: holiday.date,
        name: holiday.name,
        globalDefault: holiday.globalDefault
      }
      this.holidayFormError = ''
      this.showHolidayModal = true
    },
    closeHolidayModal () {
      if (this.savingHoliday) return
      this.showHolidayModal = false
      this.holidayForm = emptyHolidayForm()
      this.holidayFormError = ''
    },
    async saveHoliday () {
      const validationError = this.validateHolidayForm()
      if (validationError) {
        this.holidayFormError = validationError
        return
      }

      this.savingHoliday = true
      this.holidayFormError = ''
      this.errorMessage = ''
      this.successMessage = ''
      const holidayDate = this.holidayForm.holidayDate
      const targetYear = Number(holidayDate.slice(0, 4))
      const wasEditing = Boolean(this.holidayForm.originalDate)
      try {
        await api.saveHoliday({
          original_date: this.holidayForm.originalDate,
          holiday_date: holidayDate,
          name: this.holidayForm.name.trim()
        })
        this.showHolidayModal = false
        this.holidayForm = emptyHolidayForm()
        this.successMessage = wasEditing ? '休日を更新しました。' : '休日を追加しました。'
        await this.loadCalendar(targetYear, { preserveWeekly: this.weeklyDirty })
      } catch (error) {
        this.holidayFormError = this.extractError(error, '休日を保存できませんでした。')
      } finally {
        this.savingHoliday = false
      }
    },
    validateHolidayForm () {
      const date = this.parseIsoDate(this.holidayForm.holidayDate)
      if (!date) return '日付を正しく入力してください。'
      if (!this.isValidYear(date.getFullYear())) return '日付は2000年から2100年の範囲で入力してください。'
      const name = String(this.holidayForm.name || '').trim()
      if (!name) return '休日名を入力してください。'
      if (name.length > 120) return '休日名は120文字以内で入力してください。'
      const duplicate = this.holidays.some(holiday =>
        holiday.date === this.holidayForm.holidayDate &&
        holiday.date !== this.holidayForm.originalDate
      )
      if (duplicate) return '同じ日付の休日が既に登録されています。'
      return ''
    },
    openDeleteHoliday (holiday) {
      this.deleteTarget = Object.assign({}, holiday)
    },
    closeDeleteModal () {
      if (this.deletingHoliday) return
      this.deleteTarget = null
    },
    async deleteHoliday () {
      if (!this.deleteTarget) return
      const target = Object.assign({}, this.deleteTarget)
      this.deletingHoliday = true
      this.errorMessage = ''
      this.successMessage = ''
      try {
        await api.deleteHoliday({ holiday_date: target.date })
        this.deleteTarget = null
        this.successMessage = target.globalDefault
          ? '共通休日を現在の会社だけで非表示にしました。'
          : '自社休日を削除しました。'
        await this.loadCalendar(this.selectedYear, { preserveWeekly: this.weeklyDirty })
      } catch (error) {
        this.handleError(error, '休日を削除できませんでした。')
      } finally {
        this.deletingHoliday = false
      }
    },
    normalizeDays (days) {
      const values = Array.isArray(days) ? days : []
      return Array.from(new Set(values.map(Number).filter(day => this.isIsoDay(day))))
        .sort((left, right) => left - right)
    },
    normalizeHolidays (holidays) {
      const values = Array.isArray(holidays) ? holidays : []
      return values.map(holiday => ({
        id: holiday.id == null ? null : holiday.id,
        date: String(holiday.date || ''),
        name: String(holiday.name || ''),
        sourceType: String(holiday.sourceType || holiday.source_type || ''),
        globalDefault: holiday.globalDefault === true || holiday.global_default === true
      }))
        .filter(holiday => this.parseIsoDate(holiday.date) && holiday.name.trim())
        .sort((left, right) => left.date.localeCompare(right.date))
    },
    isIsoDay (value) {
      const numericValue = Number(value)
      return Number.isInteger(numericValue) && numericValue >= 1 && numericValue <= 7
    },
    isValidYear (value) {
      return Number.isInteger(Number(value)) && Number(value) >= 2000 && Number(value) <= 2100
    },
    dayLabel (value) {
      const day = this.weekDays.find(item => item.value === Number(value))
      return day ? day.label : '未設定'
    },
    parseIsoDate (value) {
      const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''))
      if (!match) return null
      const year = Number(match[1])
      const month = Number(match[2])
      const day = Number(match[3])
      const date = new Date(year, month - 1, day)
      if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null
      return date
    },
    formatHolidayDate (value) {
      const date = this.parseIsoDate(value)
      if (!date) return value
      const weekday = ['日', '月', '火', '水', '木', '金', '土'][date.getDay()]
      return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日（${weekday}）`
    },
    holidaySourceLabel (holiday) {
      if (holiday.sourceType === 'GLOBAL') return '共通休日'
      if (holiday.globalDefault) return '共通休日を自社変更'
      return '自社追加'
    },
    holidaySourceColor (holiday) {
      if (holiday.sourceType === 'GLOBAL') return 'secondary'
      return holiday.globalDefault ? 'warning' : 'info'
    },
    handleError (error, fallback) {
      this.errorMessage = this.extractError(error, fallback)
      if (this.$el && typeof this.$el.scrollIntoView === 'function') {
        this.$el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    extractError (error, fallback) {
      const data = error && error.response && error.response.data
      if (typeof data === 'string' && data.trim()) return data.trim()
      const detail = data && (data.message)
      return detail || fallback
    }
  }
}
</script>

<style scoped>
.national-holiday-switch {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  margin-top: 12px;
  padding: 12px 14px;
  border: 1px solid #d8dbe0;
  border-radius: 8px;
  background: #fbfcfd;
  cursor: pointer;
}
.national-holiday-switch input { margin-top: 3px; flex: 0 0 auto; }
.national-holiday-switch strong { display: block; }
.national-holiday-switch small { display: block; color: #5c6873; line-height: 1.6; }
.holiday-calendar-settings { min-width: 0; }
.holiday-calendar-card { min-width: 0; margin-bottom: 18px; }
.holiday-card-heading,
.calendar-section-heading,
.year-toolbar,
.holiday-list-summary { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; }
/* 見出しの行だけ横いっぱいに。伸ばさないと「変更」が右端に来ない。 */
.holiday-card-heading { width: 100%; }
.section-heading { display: flex; align-items: flex-start; gap: 12px; }
.section-heading strong { color: #17384d; font-size: 16px; }
.section-heading p,
.calendar-section-heading p,
.modal-heading p { margin: 3px 0 0; color: #6a7f8c; font-size: 12px; line-height: 1.6; }
.section-icon { display: inline-flex; width: 38px; height: 38px; flex: 0 0 auto; align-items: center; justify-content: center; border-radius: 12px; background: #e9f4fb; color: #0a6ca8; }
.calendar-loading,
.calendar-load-failed,
.holiday-empty-state { display: flex; min-height: 210px; align-items: center; justify-content: center; flex-direction: column; gap: 10px; color: #607884; text-align: center; }
.calendar-loading { flex-direction: row; }
.calendar-loading--compact { min-height: 120px; }
.calendar-load-failed p,
.holiday-empty-state p { max-width: 560px; margin: 0 0 4px; line-height: 1.6; }
.calendar-section { padding: 2px 0 24px; }
.calendar-section + .calendar-section { padding-top: 24px; border-top: 1px solid #dfe8ee; }
.calendar-section--master { padding-bottom: 0; }
.calendar-section-heading { margin-bottom: 16px; }
.calendar-section-heading h3 { margin: 0; color: #17384d; font-size: 20px; }
.section-kicker { margin: 0 0 4px !important; color: #0a6ca8 !important; font-size: 10px !important; font-weight: 800; letter-spacing: .14em; }
.weekly-setting-grid { display: grid; grid-template-columns: minmax(220px, .65fr) minmax(0, 1.35fr); gap: 14px; }
.weekly-setting-panel { min-width: 0; padding: 16px; border: 1px solid #dce8ef; border-radius: 14px; background: #f8fbfd; }
.weekly-setting-panel--statutory { border-left: 4px solid #c93442; }
.weekly-setting-panel--company { border-left: 4px solid #0a6ca8; }
.weekly-setting-panel label,
.weekly-setting-panel legend,
.holiday-editor-grid label { color: #314f62; font-size: 12px; font-weight: 800; }
.weekly-setting-panel legend { width: auto; margin: 0 0 4px; font-size: 13px; }
.weekly-setting-panel > p { margin: 0 0 12px; color: #6a7f8c; font-size: 12px; }
.weekly-setting-panel > small,
.holiday-editor-grid small { display: block; margin-top: 7px; color: #778b97; font-size: 11px; line-height: 1.5; }
.required-mark { margin-left: 3px; color: #d33d4c; }
.weekday-choice-grid { display: grid; grid-template-columns: repeat(7, minmax(56px, 1fr)); gap: 8px; }
.weekday-choice { display: grid; min-width: 0; min-height: 72px; place-items: center; gap: 2px; padding: 9px 4px; border: 1px solid #d8e5ec; border-radius: 11px; background: #fff; cursor: pointer; text-align: center; transition: .16s ease; }
.weekday-choice input { position: absolute; opacity: 0; pointer-events: none; }
.weekday-choice span { display: inline-flex; width: 30px; height: 30px; align-items: center; justify-content: center; border-radius: 50%; background: #edf4f8; color: #345468; font-size: 13px; font-weight: 800; }
.weekday-choice small { margin: 0; color: #718691; font-size: 10px; }
.weekday-choice.is-selected { border-color: #0a7abf; background: #edf8fe; box-shadow: inset 0 0 0 1px #0a7abf; }
.weekday-choice.is-selected span { background: #0a7abf; color: #fff; }
.weekday-choice.is-disabled { cursor: not-allowed; opacity: .5; }
.weekly-summary { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; margin-top: 14px; }
.weekly-summary > div { display: grid; gap: 5px; padding: 13px 14px; border: 1px solid #dce8ef; border-radius: 11px; background: #fff; }
.weekly-summary span { color: #617887; font-size: 11px; font-weight: 800; }
.weekly-summary strong { color: #17384d; font-size: 14px; }
.calendar-section-actions { display: flex; justify-content: flex-end; margin-top: 14px; }
.calendar-section-actions .btn { min-width: 180px; min-height: 42px; }
.holiday-master-heading { align-items: center; }
.year-toolbar { align-items: center; margin-bottom: 14px; padding: 12px; border: 1px solid #dce8ef; border-radius: 12px; background: #f8fbfd; }
.year-navigation { display: flex; align-items: center; gap: 8px; }
.year-navigation label { margin: 0 2px 0 6px; color: #314f62; font-size: 12px; font-weight: 800; }
.year-input { width: 100px; }
.holiday-list-summary { align-items: center; margin-bottom: 10px; }
.holiday-list-summary strong { color: #17384d; font-size: 16px; }
.holiday-list-summary span { color: #617887; font-size: 12px; font-weight: 700; }
.holiday-table-wrap { width: 100%; overflow-x: auto; border: 1px solid #dce8ef; border-radius: 12px; }
.holiday-table { width: 100%; min-width: 720px; margin: 0; border-collapse: collapse; background: #fff; }
.holiday-table th { padding: 11px 13px; border-bottom: 1px solid #dce8ef; background: #f4f8fa; color: #5c7483; font-size: 11px; font-weight: 800; text-align: left; white-space: nowrap; }
.holiday-table td { padding: 13px; border-bottom: 1px solid #edf2f5; color: #314f62; font-size: 13px; vertical-align: middle; }
.holiday-table tbody tr:last-child td { border-bottom: 0; }
.holiday-table td:first-child strong { color: #17384d; white-space: nowrap; }
.holiday-actions-column { width: 190px; }
.holiday-actions { display: flex; justify-content: flex-end; gap: 7px; }
.holiday-empty-state { min-height: 220px; border: 1px dashed #cbdce5; border-radius: 14px; background: #f9fbfc; }
.modal-mask { position: fixed; z-index: 9998; inset: 0; display: table; width: 100%; height: 100%; background: rgba(26, 45, 57, .58); }
.modal-wrapper { display: table-cell; vertical-align: middle; }
.modal-container { width: min(92vw, 680px); max-height: 90vh; margin: 0 auto; padding: 24px; overflow-y: auto; border-radius: 16px; background: #fff; box-shadow: 0 24px 60px rgba(9, 34, 50, .28); }
.modal-container--small { width: min(92vw, 560px); }
.modal-heading { margin-bottom: 18px; }
.modal-heading h2 { margin: 0; color: #17384d; font-size: 22px; }
.holiday-editor-grid { display: grid; grid-template-columns: minmax(190px, .8fr) minmax(0, 1.2fr); gap: 14px; margin-bottom: 16px; }
.delete-summary { display: grid; gap: 8px; margin: 0 0 16px; }
.delete-summary > div { display: grid; grid-template-columns: 100px minmax(0, 1fr); gap: 12px; padding: 11px 0; border-bottom: 1px solid #edf2f5; }
.delete-summary dt { color: #617887; font-size: 11px; font-weight: 800; }
.delete-summary dd { min-width: 0; margin: 0; color: #17384d; font-weight: 700; overflow-wrap: anywhere; }
.modal-actions { display: flex; justify-content: space-between; gap: 12px; margin-top: 18px; }
.modal-actions .btn { min-width: 140px; min-height: 42px; }
.modal-enter-active,
.modal-leave-active { transition: opacity .18s ease; }
.modal-enter,
.modal-leave-to { opacity: 0; }

@media (max-width: 991.98px) {
  .weekly-setting-grid { grid-template-columns: minmax(0, 1fr); }
  .weekday-choice-grid { grid-template-columns: repeat(4, minmax(56px, 1fr)); }
  .year-toolbar { align-items: stretch; flex-direction: column; }
  .year-navigation { flex-wrap: wrap; }
}

@media (max-width: 767.98px) {
  .holiday-card-heading,
  .calendar-section-heading { align-items: flex-start; flex-direction: column; }
  .holiday-master-heading .btn { width: 100%; min-height: 42px; }
  .weekday-choice-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .weekly-summary { grid-template-columns: minmax(0, 1fr); }
  .calendar-section-actions .btn { width: 100%; }
  .year-navigation { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .year-navigation label { margin: 0; align-self: center; }
  .year-input { width: 100%; }
  .holiday-table { min-width: 0; }
  .holiday-table thead { display: none; }
  .holiday-table,
  .holiday-table tbody,
  .holiday-table tr,
  .holiday-table td { display: block; width: 100%; }
  .holiday-table tr { padding: 12px; border-bottom: 1px solid #dce8ef; }
  .holiday-table tr:last-child { border-bottom: 0; }
  .holiday-table td { display: grid; grid-template-columns: minmax(92px, 36%) minmax(0, 1fr); gap: 10px; padding: 8px 0; border: 0; }
  .holiday-table td::before { color: #617887; font-size: 11px; font-weight: 800; content: attr(data-label); }
  .holiday-actions { display: flex !important; justify-content: flex-start; }
  .holiday-actions::before { flex: 0 0 92px; }
  .holiday-editor-grid { grid-template-columns: minmax(0, 1fr); }
  .modal-container { width: calc(100vw - 16px); max-height: calc(100vh - 16px); padding: 16px; }
  .modal-actions { flex-direction: column-reverse; }
  .modal-actions .btn { width: 100%; }
}
</style>
